import { WidgetWrapperComponent } from './widget-wrapper.component';
import { DynamicElementService } from '../../services/dynamicElement.service';
import { IWidget } from '../../interfaces/iWidget';
import { WidgetWrapperState } from './widgetWrapperState';
import { AwallComponentEvent } from '../../models/awallComponentEvent';
import { ComponentEvent } from '../../models/enums/componentEvent';
import { CommunicationService } from '../../services/communication.service';

export class StateTransitionHelperClass {
  public static readonly CSS_CLASSNAME_DOCKED = 'widget--is-docked';
  public static readonly CSS_CLASSNAME_UNDOCKED = 'widget--is-undocked';
  public static readonly CSS_CLASSNAME_DRAGGING = 'widget--is-dragging';

  constructor(protected readonly receiver: string = 'InformationviewComponent') {}

  /**
   * This function handles WidgetWrapperState-Transitions from oldState to newState
   */
  public transition(
    wrapper: WidgetWrapperComponent,
    oldState: WidgetWrapperState,
    newState: WidgetWrapperState,
    dynamicElementService: DynamicElementService,
    communicationService: CommunicationService,
    mousePositionX?: number): void {

    const widget: IWidget = wrapper.widgetComponentRef.instance;
    const wrapperStyle: CSSStyleDeclaration = wrapper.wrapperElement.style;
    const wrapperClasslist: DOMTokenList = wrapper.wrapperElement.classList;

    switch (newState) {
      case WidgetWrapperState.DOCKED_DRAGGING:
        this.toDocked(widget, wrapper, wrapperClasslist, oldState, mousePositionX);
        this.toDragging(wrapper, wrapperClasslist, oldState);
        break;
      case WidgetWrapperState.DOCKED_RESTING:
        this.toDocked(widget, wrapper, wrapperClasslist, oldState, mousePositionX);
        this.toResting(wrapperClasslist, oldState);
        this.resetWrapperLayout(wrapperStyle);
        this.moveWrapperToDock(wrapper, dynamicElementService, communicationService);
        this.removePlaceholder(wrapper);
        break;
      case WidgetWrapperState.UNDOCKED_DRAGGING:
        this.toUndocked(widget, wrapperClasslist, oldState);
        this.toDragging(wrapper, wrapperClasslist, oldState);
        break;
      case WidgetWrapperState.UNDOCKED_RESTING:
        this.toUndocked(widget, wrapperClasslist, oldState);
        this.toResting(wrapperClasslist, oldState);
        this.moveWrapperToDynamicElementContainer(wrapper, dynamicElementService, communicationService);
        break;
    }

    this.updateSpacers(communicationService);
  }

  /**
   * Tell infoview to update Spacers (we might have removed/added placeholders/wrappers on state-change)
   */
  protected updateSpacers(communicationService: CommunicationService) {
    const dockEvent = new AwallComponentEvent(ComponentEvent.INFOVIEW_UPDATE_SPACERS, null);
    communicationService.sendData(this.receiver, dockEvent);
  }

  /**
   * Transitions the wrapper and underlying widget to the docked state.
   * If it was already docked before (in oldState) no changes will be made.
   */
  protected toDocked(widget: IWidget, wrapper: WidgetWrapperComponent, wrapperClasslist: DOMTokenList,
    oldState: WidgetWrapperState, mousePositionX?: number): void {
    if (!this.fromDocked(oldState)) {
      // Set docked state on the underlying widget
      widget.isUndocked = false;
      // Set docked class on the wrapper
      wrapperClasslist.add(StateTransitionHelperClass.CSS_CLASSNAME_DOCKED);
      wrapperClasslist.remove(StateTransitionHelperClass.CSS_CLASSNAME_UNDOCKED);
      // Reset height to standard
      wrapper.wrapperElement.style.removeProperty('height');
      // Reset width to standard
      this.resetWrapperWidth(wrapper.wrapperElement, mousePositionX);
    }
  }

  /**
   * Transitions the wrapper and underlying widget to the undocked state.
   * If it was already undocked before (in oldState) no changes will be made.
   */
  protected toUndocked(widget: IWidget, wrapperClasslist: DOMTokenList, oldState: WidgetWrapperState): void {
    if (this.fromDocked(oldState)) {
      // Set undocked state on the underlying widget
      widget.isUndocked = true;
      // Set unddocked class on the wrapper
      wrapperClasslist.add(StateTransitionHelperClass.CSS_CLASSNAME_UNDOCKED);
      wrapperClasslist.remove(StateTransitionHelperClass.CSS_CLASSNAME_DOCKED);
    }
  }

  /**
   * Transitions the wrapper to the dragging state.
   * If it was already dragging before (in oldState) no changes will be made.
   */
  protected toDragging(wrapper: WidgetWrapperComponent, wrapperClasslist: DOMTokenList, oldState: WidgetWrapperState): void {
    if (!this.fromDragging(oldState)) {
      wrapperClasslist.add(StateTransitionHelperClass.CSS_CLASSNAME_DRAGGING);
      this.addPlaceholder(wrapper);
    }
  }

  /**
   * Transitions the wrapper to the resting state.
   * If it was already resting before (in oldState) no changes will be made.
   */
  protected toResting(wrapperClasslist: DOMTokenList, oldState: WidgetWrapperState): void {
    if (this.fromDragging(oldState)) {
      wrapperClasslist.remove(StateTransitionHelperClass.CSS_CLASSNAME_DRAGGING);
    }
  }

  /**
   * Returns 'true' if oldState was a docked state
   */
  protected fromDocked(oldState: WidgetWrapperState): boolean {
    return (oldState === WidgetWrapperState.DOCKED_DRAGGING || oldState === WidgetWrapperState.DOCKED_RESTING);
  }

  /**
   * Returns 'true' if oldState was a dragging state
   */
  protected fromDragging(oldState: WidgetWrapperState): boolean {
    return (oldState === WidgetWrapperState.DOCKED_DRAGGING || oldState === WidgetWrapperState.UNDOCKED_DRAGGING);
  }

  /**
   * Move the wrapper to the dynamicElementContainer and set position absolute so z-Index works.
   */
  protected moveWrapperToDynamicElementContainer(
    wrapper: WidgetWrapperComponent,
    dynamicElementService: DynamicElementService,
    communicationService: CommunicationService): void {

    if (wrapper.isInDock()) {
      // Tell infoview to remove this wrapper from its viewcontainer
      this.sendRemoveWrapperEvent(communicationService, wrapper);

      // Insert it in dynamicElementContainer and set position = absolute so z-Index works
      dynamicElementService.insert(wrapper.wrapperComponentRef);
      wrapper.wrapperComponentRef.location.nativeElement.style.position = 'absolute';
    }
  }

  protected sendRemoveWrapperEvent(communicationService: CommunicationService, { widgetName }: WidgetWrapperComponent) {
    const data = { widgetName };
    const dockEvent = new AwallComponentEvent(ComponentEvent.INFOVIEW_REMOVE_WRAPPER, data);
    communicationService.sendData(this.receiver, dockEvent);
  }

  /**
   * Move the wrapper back from the dynamicElementContainer to the dock
   */
  protected moveWrapperToDock(
    wrapper: WidgetWrapperComponent,
    dynamicElementService: DynamicElementService,
    communicationService: CommunicationService): void {

    if (!wrapper.isInDock()) {
      // Remove the wrapper form dynamicElementContainer and remove the position = absolute
      dynamicElementService.remove(wrapper.wrapperComponentRef);
      wrapper.wrapperComponentRef.location.nativeElement.style.position = '';

      // Tell informationview to insert the wrapper to its viewcontainer
      this.sendInsertWrapperEvent(communicationService, wrapper);

      this.replaceWithPlaceholder(wrapper);
    }
  }

  protected replaceWithPlaceholder(wrapper: WidgetWrapperComponent) {
    // Replace the placeholder element with the wrapper in DOM
    wrapper.widgetContainer.replaceChild(wrapper.wrapperElement.parentElement, wrapper.placeholderElement);
  }

  protected sendInsertWrapperEvent(
    communicationService: CommunicationService,
    { widgetName, wrapperComponentRef }: WidgetWrapperComponent,
  ) {
    const data = { widgetName, wrapperComponentRef };
    const dockEvent = new AwallComponentEvent(ComponentEvent.INFOVIEW_INSERT_WRAPPER, data);
    communicationService.sendData(this.receiver, dockEvent);
  }

  /**
   * Reset the wrapper layout when docking as it might have been change by dragging/resizing actions
   */
  protected resetWrapperLayout(wrapperStyle: CSSStyleDeclaration): void {
    wrapperStyle.top = '0';
    wrapperStyle.left = '0';
    wrapperStyle.width = '';
    wrapperStyle.height = '';
  }

  /**
   * Reset the width of the wrapper-element to its default. This can require additional positioning of the
   * widget so the mouse-cursor stays on the widget header while changing the widgets width.
   * Careful: Using a css-transition will disturb the correct calculation of the new width and visual appearance
   */
  protected resetWrapperWidth(wrapperElement: HTMLElement, mousePositionX?: number) {
    // This removes the width-css-property and tells us if it was set or not
    const wrapperHadCustomWidth = (wrapperElement.style.removeProperty('width') !== '');

    // If the wrapper had a custom width before we reset it, the width is gonna change and
    // we need to correct the left-attribute of the wrapper to make sure it stays under the mouse cursor.
    // The correction will be applied here and also in the drag-function in the widget-wrapper until
    // the drag ends.
    if (wrapperHadCustomWidth) {
      const mouseX = (mousePositionX) ? mousePositionX : 0;
      const wrapperCenterX = wrapperElement.offsetLeft + (0.5 * wrapperElement.offsetWidth);
      // Calculate how much we need to correct the left-value to move the horizontal center
      // of the wrapper - header to the mouse position
      const correction = mouseX - wrapperCenterX;
      if (correction > 0) {
        wrapperElement['correctionLeft'] = correction;
        const newLeft = wrapperElement.offsetLeft + correction;
        // Apply new left value directly. Otherwise it requires the user to drag the widget to apply the correction
        wrapperElement.style.left = `${newLeft}px`;
      }
    }
  }

  protected addPlaceholder(wrapper: WidgetWrapperComponent): void {
    const placeholder: HTMLElement = wrapper.placeholderElement;
    const wrapperComponent: HTMLElement = wrapper.wrapperElement.parentElement;
    const container: HTMLElement = wrapper.widgetContainer;

    if (container.contains(wrapperComponent)) {
      container.insertBefore(placeholder, wrapperComponent);
    }
  }

  protected removePlaceholder(wrapper: WidgetWrapperComponent): void {
    const placeholder: HTMLElement = wrapper.placeholderElement;
    const container: HTMLElement = wrapper.widgetContainer;

    if (container.contains(placeholder)) {
      container.removeChild(placeholder);
    }
  }
}

export const StateTransitionHelper = new StateTransitionHelperClass();
